import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle, AlertCircle } from 'lucide-react';
import type { RiskLevel } from '@bcc/shared-types';
import { cn } from '../utils/cn';

export interface RiskIndicatorProps {
  level: RiskLevel;
  probability?: number;
  label?: string;
  showIcon?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function RiskIndicator({
  level,
  probability,
  label,
  showIcon = true,
  size = 'md',
  className,
}: RiskIndicatorProps) {
  const config = {
    HIGH: {
      color: 'text-green-600',
      bg: 'bg-green-100',
      border: 'border-green-200',
      icon: CheckCircle,
      label: 'High Approval Likelihood',
    },
    MEDIUM: {
      color: 'text-yellow-600',
      bg: 'bg-yellow-100',
      border: 'border-yellow-200',
      icon: AlertCircle,
      label: 'Moderate Risk',
    },
    LOW: {
      color: 'text-red-600',
      bg: 'bg-red-100',
      border: 'border-red-200',
      icon: AlertTriangle,
      label: 'Low Approval Likelihood',
    },
  };

  const { color, bg, border, icon: Icon, label: defaultLabel } = config[level];

  const sizes = {
    sm: {
      container: 'p-2',
      icon: 'h-4 w-4',
      text: 'text-sm',
      probability: 'text-lg',
    },
    md: {
      container: 'p-4',
      icon: 'h-6 w-6',
      text: 'text-base',
      probability: 'text-2xl',
    },
    lg: {
      container: 'p-6',
      icon: 'h-8 w-8',
      text: 'text-lg',
      probability: 'text-4xl',
    },
  };

  const s = sizes[size];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={cn(
        'rounded-xl border',
        bg,
        border,
        s.container,
        className
      )}
    >
      <div className="flex items-center gap-3">
        {showIcon && <Icon className={cn(s.icon, color)} />}

        <div className="flex-1">
          <p className={cn('font-medium', color, s.text)}>
            {label || defaultLabel}
          </p>
          {probability !== undefined && (
            <p className={cn('font-bold', color, s.probability)}>
              {probability}%
              <span className={cn('font-normal', s.text, 'ml-1')}>
                approval probability
              </span>
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
